import { template as _$template } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<canvas class=cockpit-panel data-testid=cockpit-panel aria-hidden=true>`);
import { createEffect, onCleanup, onMount } from "solid-js";
import { cockpitFrameFor, cockpitFrameKey, NEUTRAL_SURFACES } from "./cockpit-frame.ts";
import { createDashSnapshotGate, dashArtBounds, dashFrameStats, snapshotDashFrame } from "./cockpit-dash-quad.ts";
import { loadCockpit } from "./cockpit-composition.ts";
import { readingsFromHud } from "./instrument-values.ts";
import { resolveKillCamRect } from "./killcam-layout.ts";
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, presentTransform, visibleGauges } from "./panel-layout.ts";
import { drawComposition, drawGauges, drawOps, presentCockpit } from "./panel-render.ts";
import { hitOverlaysFor } from "./cockpit-hits.ts";
import { currentDevicePixelRatio, observeViewMetrics } from "../three/view-metrics.ts";

/**
 * Dedup cache keyed by aircraft, module-scoped so switching views (or
 * remounting the route) does not refetch a composition and its sheets.
 */
const cockpitCache = new Map();
function cachedCockpit(aircraft) {
  const cached = cockpitCache.get(aircraft);
  if (cached) return cached;
  const pending = loadCockpit(aircraft);
  cockpitCache.set(aircraft, pending);
  pending.catch(() => cockpitCache.delete(aircraft));
  return pending;
}

/**
 * `createImageBitmap`, or `undefined` where the browser has none — in which case
 * `snapshotDashFrame` falls back to publishing the canvas itself, which draws
 * correctly and only costs the clean ownership the snapshot exists to give.
 *
 * Read through a function rather than captured at module scope so a test can run
 * this file in an environment that has no `createImageBitmap` at all.
 */
function imageBitmapSource() {
  return typeof createImageBitmap === "function" ? source => createImageBitmap(source) : undefined;
}
function logicalCanvas() {
  const canvas = document.createElement("canvas");
  canvas.width = LOGICAL_WIDTH;
  canvas.height = LOGICAL_HEIGHT;
  return canvas;
}

/**
 * How often the composed frame is read back for the probe, in milliseconds.
 *
 * The readback is what makes "textured but blank" a *measurable* state rather
 * than an invisible one (`dashFrameStats`), and it is the one thing in this file
 * that costs something the picture does not need: `getImageData` over 320x200 is
 * a CPU sync on a canvas the browser may be keeping on the GPU. Twice a second
 * is far more often than a human reads a probe and a twenty-fourth of the rate
 * the panel repaints at, so it is free in the frame budget and still fresh.
 */
const DASH_INK_SAMPLE_MS = 500;

/** Every fourth pixel — see `dashFrameStats`; the question is "is there paint here", not a histogram. */
const DASH_INK_STRIDE = 4;

/**
 * The in-cockpit view: the original's own draw program, replayed at 1:1 into
 * a **320x200** offscreen canvas and presented over the WebGPU viewport with
 * an integer nearest-neighbour upscale and the VGA 1:1.2 pixel-aspect
 * correction, anchored bottom-centre (AD-009's "authentic backend" shape).
 *
 * Three canvases, because the work splits three ways by how often it changes:
 *
 * - one `background` (320x200, offscreen) **per view**, cached by
 *   `cockpitFrameKey` — the aircraft's baked composition for the glance being
 *   looked at, and for the forward view its guns-idle and guns-firing
 *   variants. Each is ~40-110 sprite/polygon ops and is composed once, which
 *   is what lets the muzzle flash strobe at 11 Hz and a glance snap
 *   instantly for the cost of one `drawImage`.
 * - `frame` (320x200, offscreen) — the chosen background plus the instrument
 *   row and its live needles. Rebuilt per HUD update (~12 Hz) or whenever the
 *   view or the flash changes.
 * - the visible `<canvas>` — one `drawImage` of `frame`, scaled.
 *
 * The needles are drawn *in the 320x200 frame*, not on the presented one, so
 * they are the same one-pixel needles the original drew rather than smooth
 * vectors floating over pixel art.
 */
export function CockpitPanel(props) {
  let canvasRef;
  let art;
  let backgrounds = new Map();
  const frame = logicalCanvas();
  /** Keeps one snapshot in flight at a time — see `snapshotDashFrame`. */
  const snapshots = createDashSnapshotGate();
  /** When the composed frame was last read back for the probe (`DASH_INK_SAMPLE_MS`). */
  let inkSampledAt = Number.NEGATIVE_INFINITY;

  /**
   * What is actually painted in the frame about to be published, or `undefined`
   * when this repaint is inside the throttle window.
   *
   * Measured off the very canvas the snapshot is taken from, one statement
   * earlier, so the numbers and the picture cannot be of different frames.
   */
  function inkOf(frameCtx) {
    const now = typeof performance === "undefined" ? Date.now() : performance.now();
    if (now - inkSampledAt < DASH_INK_SAMPLE_MS) return undefined;
    inkSampledAt = now;
    try {
      return dashFrameStats(frameCtx.getImageData(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT).data, DASH_INK_STRIDE);
    } catch {
      // A tainted or unreadable canvas costs the probe its reading and nothing
      // else — the dashboard is drawn from the snapshot, not from this.
      return undefined;
    }
  }

  /** One composed canvas per view, built on first sight of that view and kept. */
  function backgroundFor(key, ops) {
    const cached = backgrounds.get(key);
    if (cached) return cached;
    const canvas = logicalCanvas();
    const ctx = canvas.getContext("2d");
    if (!ctx || !art) return undefined;
    drawComposition(ctx, art, ops);
    backgrounds.set(key, canvas);
    return canvas;
  }
  function redraw() {
    if (!props.active || !art) return;
    const frameCtx = frame.getContext("2d");
    if (!frameCtx) return;
    frameCtx.clearRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
    frameCtx.imageSmoothingEnabled = false;
    const mode = props.mode ?? "painted";
    const surfaces = props.surfaces ?? NEUTRAL_SURFACES;
    // A glance with no art of its own (`up`, and every glance at all in
    // `modelled` mode) shows the bare 3D view.
    const view = cockpitFrameFor(art.composition, props.glance, props.gunFlash, surfaces, mode);
    if (view) {
      const background = backgroundFor(cockpitFrameKey(props.glance, props.gunFlash, surfaces, mode), view.ops);
      if (background) frameCtx.drawImage(background, 0, 0);
      if (view.showGauges) {
        const gauges = visibleGauges(art.composition.gauges, props.realisticInstruments);
        drawGauges(frameCtx, art, gauges, readingsFromHud(props.hud));
      }
      // Last, so a hole sits on top of the instruments the way it sits on top
      // of everything else in the cockpit. Not folded into the cached
      // background: the mask changes as the flight goes on, and an overlay is
      // only 1-17 sprites.
      for (const overlay of hitOverlaysFor(art.composition.hits, props.glance, props.hitMarks ?? 0)) {
        drawOps(frameCtx, art, overlay);
      }
    }
    if (mode === "modelled") {
      // The finished frame goes to the quad instead of to the screen, and this
      // element is **removed from the page** rather than left cleared — see the
      // `classList` below. Nothing here touches the visible canvas, which is why
      // the size checks moved out of the top of this function: a `display: none`
      // canvas has no size, and the composition must not stop when the panel
      // stops being shown.
      //
      // A **snapshot**, not this canvas: after the copy neither side owns
      // anything the other is touching, so the panel is free to clear and
      // repaint while the GPU reads (`scene-cockpit-3d.ts`). `dashArtBounds` is
      // measured off the *ops*, not off the pixels, so it is the same rectangle
      // whether or not a gauge happened to be drawn this frame and the quad's
      // geometry does not breathe with the instrument row.
      if (props.dashFeed && view) {
        snapshotDashFrame(props.dashFeed, snapshots, frame, dashArtBounds(view.ops, art.composition.sheetFrames), imageBitmapSource(), inkOf(frameCtx));
      }
      return;
    }
    presentToScreen();
  }

  /**
   * The composed 320x200 frame, letterboxed onto the visible canvas.
   *
   * Split out of `redraw` because it is the one half that touches the *screen*:
   * everything above it composes into the offscreen frame and is shared with
   * `modelled` mode, which never reaches this at all.
   */
  function presentToScreen() {
    if (!canvasRef) return;
    const width = canvasRef.clientWidth;
    const height = canvasRef.clientHeight;
    if (width === 0 || height === 0) return;
    // **The backing store is device pixels; every number below stays CSS
    // pixels.** This panel is a DOM canvas laid exactly over the WebGPU one, and
    // the two have to stay registered: `cockpit-projection.ts` puts the camera's
    // optical axis on the gunsight this file paints, using
    // `presentTransform(cssWidth, cssHeight)` — the same call, on the same
    // numbers, from `flight-view.ts`'s deliberately-CSS-pixel
    // `viewportWidthPx/HeightPx`. Feeding *device* pixels to `presentTransform`
    // here would move the painted gunsight by the pixel ratio while the axis
    // stayed put, which is precisely the fault `flight-view.ts` calls "the bug"
    // where it explains why the rig keeps CSS pixels. So the ratio buys
    // resolution and nothing else: it scales the raster and is then invisible.
    //
    // Before this, the panel's backing store was its CSS box, so on a 2x display
    // the painted cockpit was drawn at half the resolution of the 3D scene
    // behind it and smoothly upscaled by the compositor — `.cockpit-panel`
    // carries no `image-rendering`, unlike `.shell__screen` — which is a blurred
    // dashboard over a native-resolution world. Measured on the live page at
    // `dpr: 2`: the 3D canvas backed 2560x1600 while this one backed 1280x800,
    // at every window shape tried. Because the upscale here is *smoothed* rather
    // than nearest-neighbour, this one gains at whole ratios too, which is the
    // opposite of the shell's case (`render/present.ts`'s `shellBackingSize`).
    //
    // The registration was checked rather than assumed: over the instrument row
    // at `dpr: 2`, before against after, the difference is minimised at **zero**
    // offset on both axes and rises symmetrically either side of it — so the
    // dashboard is in exactly the same place and only its sharpness moved.
    const ratio = currentDevicePixelRatio();
    const bufferWidth = Math.max(1, Math.floor(width * ratio));
    const bufferHeight = Math.max(1, Math.floor(height * ratio));
    if (canvasRef.width !== bufferWidth) canvasRef.width = bufferWidth;
    if (canvasRef.height !== bufferHeight) canvasRef.height = bufferHeight;
    const ctx = canvasRef.getContext("2d");
    if (!ctx) return;
    // Set absolutely rather than with `scale()`, which would compound: `redraw`
    // runs on every HUD change, and the `canvas.width` assignment that used to
    // reset the transform for free is now guarded (assigning it clears the
    // canvas, and this panel is repainted far more often than it is resized).
    // `presentCockpit` opens with a `clearRect` over the same CSS-pixel box, so
    // the transparency this overlay depends on is still established every frame.
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    presentCockpit(ctx, frame, width, height, presentTransform(width, height));
    // After presenting, so the hole is cut through the finished picture rather
    // than through the 320x200 composite, which is letterboxed on the way here.
    // CSS pixels, under the ratio transform — the same units `KillCamFrame.tsx`
    // measures its own layer in and `scene-killcam.ts` scissors with, which is
    // what keeps the hole, the border and the rendered inset one rectangle.
    if (props.killCamHole) {
      const hole = resolveKillCamRect(props.killCamPlacement ?? null, width, height);
      if (hole) ctx.clearRect(hole.x, hole.y, hole.width, hole.height);
    }
  }
  onMount(() => {
    let cancelled = false;
    // Size and density, because the backing store above is sized from both.
    const stopViewMetrics = canvasRef ? observeViewMetrics(canvasRef, () => redraw()) : undefined;
    onCleanup(() => {
      cancelled = true;
      stopViewMetrics?.();
    });
    createEffect(() => {
      const aircraft = props.aircraft;
      cachedCockpit(aircraft).then(loaded => {
        if (cancelled) return;
        art = loaded;
        backgrounds = new Map();
        redraw();
      }).catch(() => {
        // Best-effort: a failed baked-art fetch leaves the panel blank
        // rather than throwing inside the render/effect loop.
      });
    });
  });
  createEffect(() => {
    // Re-run whenever any of these change; the redraw is a no-op while
    // `active` is false or the composition has not resolved yet.
    void props.hud;
    void props.realisticInstruments;
    void props.active;
    void props.glance;
    void props.gunFlash;
    void props.hitMarks;
    void props.surfaces;
    void props.killCamHole;
    void props.killCamPlacement;
    void props.mode;
    redraw();
  });
  return (() => {
    var _el$ = _tmpl$();
    var _ref$ = canvasRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$) : canvasRef = _el$;
    _$effect(_p$ => {
      var _v$ = !!(!props.active || (props.mode ?? "painted") === "modelled"),
        _v$2 = cockpitFrameKey(props.glance, props.gunFlash, props.surfaces ?? NEUTRAL_SURFACES, props.mode ?? "painted");
      _v$ !== _p$.e && _el$.classList.toggle("cockpit-panel--hidden", _p$.e = _v$);
      _v$2 !== _p$.t && _$setAttribute(_el$, "data-cockpit-frame", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}